import { fetchHealth, setUnauthorizedHandler, type Health, type Track } from "./api.js";
import { AutoMix } from "./automix/controller.js";
import { Mixer } from "./audio/mixer.js";
import { AutoMixPanel } from "./ui/automix-panel.js";
import { DeckPanel } from "./ui/deck-panel.js";
import { escapeHtml } from "./ui/format.js";
import { CurationPanel } from "./ui/curation.js";
import { LibraryBrowser } from "./ui/library.js";
import { LoginGate } from "./ui/login.js";
import { MixerPanel } from "./ui/mixer-panel.js";
import { TransportBar } from "./ui/toolbar.js";

function el(id: string): HTMLElement {
  const node = document.getElementById(id);
  if (!node) throw new Error(`missing element #${id}`);
  return node;
}

function renderProblems(h: Health): string {
  const problems: string[] = [];
  // Name the roots that are actually broken: with several configured, "the
  // library is missing" is not enough to act on.
  for (const root of h.library_roots.filter((r) => !r.ready)) {
    problems.push(
      `Library root <code>${escapeHtml(root.name)}</code> ` +
        `(<code>${escapeHtml(root.path)}</code>) is not a directory. ` +
        `Check the mount, or set LIBRARY_PATHS.`,
    );
  }
  if (!h.ffmpeg) {
    problems.push("ffmpeg is not on PATH — track analysis will fail until it is installed.");
  }
  if (problems.length === 0) return "";
  return problems.map((p) => `<div class="banner warn">${p}</div>`).join("");
}

async function main(): Promise<void> {
  // Nothing else is built until the browser holds a session: an unauthenticated
  // page should not start fetching a library it is not allowed to read.
  const gate = new LoginGate();
  setUnauthorizedHandler(() => gate.challenge());
  try {
    await gate.require();
  } catch (err) {
    el("banners").innerHTML =
      `<div class="banner bad">Could not reach the server: ${escapeHtml(String(err))}</div>`;
    return;
  }

  // The longest region the decks may ask for. Read from the server rather than
  // assumed, because it clamps over-long requests silently.
  let maxSegmentSeconds: number | null = null;
  try {
    const health = await fetchHealth();
    el("version").textContent = `v${health.version}`;
    el("banners").innerHTML = renderProblems(health);
    maxSegmentSeconds = health.max_segment_seconds ?? null;
  } catch (err) {
    el("banners").innerHTML =
      `<div class="banner bad">Backend unreachable: ${escapeHtml(String(err))}</div>`;
    return;
  }

  let library: LibraryBrowser | null = null;
  const selected = () => library?.selected() ?? null;

  const decks = [
    new DeckPanel(el("deck-a"), "A", selected, maxSegmentSeconds),
    new DeckPanel(el("deck-b"), "B", selected, maxSegmentSeconds),
  ];

  const mixer = new Mixer(["A", "B"]);
  const mixerPanel = new MixerPanel(el("mixer"), mixer, ["A", "B"]);

  for (const panel of decks) {
    panel.deck.output.connect(mixer.channel(panel.deck.id).input);
    panel.pendingDrop = (trackId) => library?.byTrackId(trackId) ?? null;
  }

  const [deckA, deckB] = decks;
  if (!deckA || !deckB) throw new Error("both decks are required");

  // Auto-mix drives the strips and crossfader itself, so fold them away while
  // it is running - on a laptop they are what pushes the library off screen.
  // Only on the arm/disarm edge, so a manual toggle is not fought over.
  let wasArmed = false;
  const automix = new AutoMix(deckA.deck, deckB.deck, mixer, (snapshot) => {
    automixPanel?.update(snapshot);
    if (snapshot.armed !== wasArmed) {
      wasArmed = snapshot.armed;
      mixerPanel.setCollapsed(snapshot.armed);
    }
  });
  let automixPanel: AutoMixPanel | null = null;
  automixPanel = new AutoMixPanel(el("automix"), automix, selected);

  const transport = new TransportBar(el("transport"), decks, mixer, selected);

  // Any hands-on control hands that transition back to the operator (X2).
  const yieldToOperator = () => automix.notifyManualControl();
  for (const panel of decks) panel.onManualControl = yieldToOperator;
  mixerPanel.onManualControl = yieldToOperator;
  transport.onManualControl = yieldToOperator;

  // Clicking a library row only selects now; loading is explicit, because with
  // two decks there is no longer an obvious "somewhere" to load into.
  const onSelect = (_track: Track) => {};

  // The tag filter drives the auto-mix pool as well as the list. One control,
  // so what you see and what auto-mix can reach are the same set — the split
  // between them is exactly the kind of divergence that has bitten here before.
  library = new LibraryBrowser(el("library"), onSelect, (tag) => {
    void automix.refreshLibrary(tag).catch((err: unknown) => {
      el("banners").innerHTML += `<div class="banner bad">${escapeHtml(String(err))}</div>`;
    });
  });
  const curation = new CurationPanel(el("curation"), () => {
    void library?.refresh();
    void automix.refreshLibrary();
  });

  try {
    await library.refresh();
    await automix.refreshLibrary();
    await curation.refresh();
  } catch (err) {
    el("banners").innerHTML += `<div class="banner bad">${escapeHtml(String(err))}</div>`;
  }

  bindKeyboard(decks, mixerPanel, automix);

  // One shared loop for both decks. Position comes from the audio clock, so it
  // has to be polled; a second loop would just double the layout work.
  const frame = () => {
    for (const panel of decks) panel.renderFrame();
    mixerPanel.renderFrame();
    automixPanel?.renderFrame();
    transport.renderFrame();
    requestAnimationFrame(frame);
  };
  requestAnimationFrame(frame);
}

/**
 * Whether a keystroke belongs to whatever is focused rather than to the app.
 *
 * Only genuine text entry counts. Testing `tagName === "INPUT"` swallowed every
 * shortcut once a fader had been clicked, because a range input is an INPUT —
 * so touching a fader silently disabled nudge until you clicked elsewhere.
 */
function isTextEntry(target: EventTarget | null): boolean {
  const element = target as HTMLElement | null;
  if (!element) return false;
  if (element.isContentEditable) return true;

  const tag = element.tagName;
  if (tag === "TEXTAREA" || tag === "SELECT") return true;
  if (tag !== "INPUT") return false;

  const type = (element as HTMLInputElement).type;
  // Ranges, checkboxes, buttons and the like have no use for letter keys, and
  // arrow keys still reach them natively because no shortcut binds arrows.
  return !["range", "checkbox", "radio", "button", "submit", "reset", "color"].includes(type);
}

function bindKeyboard(decks: DeckPanel[], mixer: MixerPanel, automix: AutoMix): void {
  const held = new Set<string>();

  document.addEventListener("keydown", (event) => {
    if (isTextEntry(event.target)) return;
    if (event.metaKey || event.ctrlKey || event.altKey) return;

    const [a, b] = decks;
    switch (event.key.toLowerCase()) {
      case "1":
        event.preventDefault();
        void a?.deck.togglePlay();
        return;
      case "2":
        event.preventDefault();
        void b?.deck.togglePlay();
        return;
      case "a":
        event.preventDefault();
        automix.toggle();
        return;
      case "m":
        event.preventDefault();
        automix.mixItUp();
        return;
      case "n":
        event.preventDefault();
        void automix.blendNow("smooth");
        return;
      case "b":
        event.preventDefault();
        void automix.blendNow("aggressive");
        return;
      case "[":
        event.preventDefault();
        mixer.nudgeCrossfader(-0.05);
        return;
      case "]":
        event.preventDefault();
        mixer.nudgeCrossfader(0.05);
        return;
      case "\\":
        event.preventDefault();
        mixer.setCrossfader(0);
        return;
      case "q":
      case "w":
      case "o":
      case "p": {
        // Nudge repeats while held; keydown fires repeatedly, so only the first
        // one should change the rate. Keyed on the lowercased name so that a
        // shift pressed or released mid-nudge cannot orphan the entry and
        // leave the deck running fast forever.
        const key = event.key.toLowerCase();
        if (held.has(key)) return;
        held.add(key);
        event.preventDefault();
        const deck = key === "q" || key === "w" ? a : b;
        deck?.deck.startNudge(key === "q" || key === "o" ? -1 : 1);
        return;
      }
      default:
        return;
    }
  });

  document.addEventListener("keyup", (event) => {
    const key = event.key.toLowerCase();
    if (!held.delete(key)) return;
    const [a, b] = decks;
    const deck = key === "q" || key === "w" ? a : b;
    deck?.deck.endNudge();
  });

  // A dropped keyup (window blur mid-nudge) would leave a deck running fast.
  window.addEventListener("blur", () => {
    held.clear();
    for (const panel of decks) panel.deck.endNudge();
  });
}

void main();
